import { zodResolver } from '@hookform/resolvers/zod';
import { type SignInOutput, useConfirmSignIn } from 'korint-sdk/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';

import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../forms/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../forms/input-otp';
import { Text } from '@folio/components/ui/text';

export function TotpChallengeForm({ email, onSuccess }: { email: string; onSuccess: () => void }) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const { confirmSignIn, isLoading } = useConfirmSignIn({
    onSuccess: (result: SignInOutput | undefined) => {
      if (result?.isSignedIn) {
        onSuccess();
      } else {
        setError(t('authTotpChallenge.errorGeneric'));
      }
    },
    onError: err => setError(mapTotpError(err, t)),
  });

  const form = useForm<{ code: string }>({
    defaultValues: { code: '' },
    resolver: zodResolver(z.object({ code: z.string().length(6) })),
  });

  const handleSubmit = form.handleSubmit(async ({ code }) => {
    setError(null);
    await confirmSignIn({ challengeResponse: code });
  });

  return (
    <Form {...form}>
      <Text size="sm" tone="muted" className="mb-4">
        {t('authTotpChallenge.subtitle', { email })}
      </Text>
      {error && (
        <Alert className="mb-4" variant="destructive">
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem className="items-center">
              <FormControl>
                <InputOTP maxLength={6} autoFocus {...field}>
                  <InputOTPGroup>
                    {Array.from({ length: 6 }, (_, index) => (
                      <InputOTPSlot key={`totp-slot-${index}`} index={index} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          className="w-full"
          type="submit"
          loading={isLoading}
          disabled={form.watch('code').length < 6}
        >
          {t('authTotpChallenge.submit')}
        </Button>
      </form>
    </Form>
  );
}

function mapTotpError(err: unknown, t: TFunction): string {
  if (!(err instanceof Error)) {
    return t('authTotpChallenge.errorGeneric');
  }
  const name = (err as Error & { name?: string }).name ?? '';
  if (name === 'CodeMismatchException') return t('authTotpChallenge.errorInvalidCode');
  if (name === 'ExpiredCodeException') return t('authTotpChallenge.errorCodeExpired');
  if (name === 'LimitExceededException' || name === 'TooManyRequestsException') {
    return t('authTotpChallenge.errorRateLimited');
  }
  return err.message || t('authTotpChallenge.errorGeneric');
}
